import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, Grid, Alert, Box, Typography,
  CircularProgress, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import IconButton from '@mui/material/IconButton';
import { documentAPI, driverAPI, helperAPI } from '../../services/api';
import { catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import type { DocumentRecord, EntityType } from '../../constants/documentTypes';
import { formatFileSize } from '../../constants/documentTypes';

interface DriverDocumentsPackDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  entityId: string;
  entityLabel?: string;
  /** Same pack for drivers and helpers */
  entityType?: Extract<EntityType, 'driver' | 'helper'>;
}

type SlotKey = 'aadhaar_front' | 'aadhaar_back' | 'photo' | 'driving_license';

const FILE_SLOTS: { key: SlotKey; label: string; required?: boolean }[] = [
  { key: 'aadhaar_front', label: 'Aadhaar Front', required: true },
  { key: 'aadhaar_back', label: 'Aadhaar Back', required: true },
  { key: 'photo', label: 'Photo', required: true },
  { key: 'driving_license', label: 'Driving License (DL)', required: true },
];

function toDateInput(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const acceptFile = (file: File | null, onError: (msg: string) => void): File | null => {
  if (!file) return null;
  const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  if (!allowed.includes(file.type) && !/\.(pdf|jpe?g|png)$/i.test(file.name)) {
    onError('Only PDF, JPG, and PNG files are allowed');
    return null;
  }
  return file;
};

const FileSlot: React.FC<{
  label: string;
  required?: boolean;
  file: File | null;
  existingName?: string;
  onPick: (file: File | null) => void;
}> = ({ label, required, file, existingName, onPick }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Paper
      elevation={0}
      onClick={() => inputRef.current?.click()}
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: `1.5px dashed ${brandColors.border}`,
        bgcolor: '#fafafa',
        cursor: 'pointer',
        height: '100%',
        '&:hover': { borderColor: brandCss.primary, bgcolor: `${brandCss.primary}06` },
      }}
    >
      <input
        ref={inputRef}
        hidden
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
      <Typography fontWeight={700} fontSize={13} sx={{ mb: 0.75 }}>
        {label}{required ? ' *' : ''}
      </Typography>
      {file || existingName ? (
        <Box>
          <Typography fontSize={12.5} fontWeight={600} noWrap title={file?.name || existingName}>
            {file?.name || existingName}
          </Typography>
          {file && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(file.size)}
            </Typography>
          )}
          {file && (
            <Button
              size="small"
              sx={{ mt: 0.5, textTransform: 'none' }}
              onClick={(e) => { e.stopPropagation(); onPick(null); }}
            >
              Remove
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: brandColors.textMuted }}>
          <CloudUploadIcon sx={{ fontSize: 22, color: brandCss.primary }} />
          <Typography fontSize={12}>PDF / JPG / PNG</Typography>
        </Box>
      )}
    </Paper>
  );
};

/**
 * One-screen pack for driver/helper: Aadhaar front/back, photo, DL + valid till, two references.
 */
export const DriverDocumentsPackDialog: React.FC<DriverDocumentsPackDialogProps> = ({
  open,
  onClose,
  onSaved,
  entityId,
  entityLabel,
  entityType = 'driver',
}) => {
  const personLabel = entityType === 'helper' ? 'Helper' : 'Driver';
  const entityApi = entityType === 'helper' ? helperAPI : driverAPI;

  const [files, setFiles] = useState<Record<SlotKey, File | null>>({
    aadhaar_front: null,
    aadhaar_back: null,
    photo: null,
    driving_license: null,
  });
  const [existing, setExisting] = useState<Partial<Record<SlotKey, DocumentRecord>>>({});
  const [dlValidTill, setDlValidTill] = useState('');
  const [reference1Name, setReference1Name] = useState('');
  const [reference1Mobile, setReference1Mobile] = useState('');
  const [reference2Name, setReference2Name] = useState('');
  const [reference2Mobile, setReference2Mobile] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entityId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setFiles({ aadhaar_front: null, aadhaar_back: null, photo: null, driving_license: null });
      try {
        const [docsRes, personRes] = await Promise.all([
          documentAPI.list({ entityType, entityId }),
          entityApi.getById(entityId),
        ]);
        if (cancelled) return;
        const docs = (docsRes.data || []) as DocumentRecord[];
        const byType: Partial<Record<SlotKey, DocumentRecord>> = {};
        docs.forEach((d) => {
          if (d.documentType === 'aadhaar_front'
            || d.documentType === 'aadhaar_back'
            || d.documentType === 'photo'
            || d.documentType === 'driving_license') {
            const prev = byType[d.documentType as SlotKey];
            if (!prev || (d.updatedAt && prev.updatedAt && d.updatedAt > prev.updatedAt)) {
              byType[d.documentType as SlotKey] = d;
            } else if (!prev) {
              byType[d.documentType as SlotKey] = d;
            }
          }
        });
        setExisting(byType);
        const person = personRes.data || {};
        setDlValidTill(toDateInput(
          byType.driving_license?.expiryDate || (entityType === 'driver' ? person.licenseExpiry : undefined),
        ));
        setReference1Name(String(person.reference1Name || ''));
        setReference1Mobile(String(person.reference1Mobile || ''));
        setReference2Name(String(person.reference2Name || ''));
        setReference2Mobile(String(person.reference2Mobile || ''));
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
          setError(msg || `Failed to load ${personLabel.toLowerCase()} documents`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, entityId, entityType, entityApi, personLabel]);

  const setSlotFile = (key: SlotKey, file: File | null) => {
    const next = acceptFile(file, setError);
    if (file && !next) return;
    setError('');
    setFiles((prev) => ({ ...prev, [key]: next }));
  };

  const handleSubmit = async () => {
    if (!entityId) return;

    const missing = FILE_SLOTS.filter((s) => s.required && !files[s.key] && !existing[s.key]);
    if (missing.length) {
      setError(`Upload required: ${missing.map((m) => m.label).join(', ')}`);
      return;
    }
    if (!dlValidTill) {
      setError('Enter Valid till date for Driving License');
      return;
    }
    if (!reference1Name.trim() || !reference1Mobile.trim()) {
      setError('Reference 1 name and mobile are required');
      return;
    }
    if (!reference2Name.trim() || !reference2Mobile.trim()) {
      setError('Reference 2 name and mobile are required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      for (const slot of FILE_SLOTS) {
        const file = files[slot.key];
        if (!file) continue;
        const formData = new FormData();
        formData.append('entityType', entityType);
        formData.append('entityId', entityId);
        formData.append('documentType', slot.key);
        formData.append('file', file);
        if (slot.key === 'driving_license') {
          formData.append('expiryDate', dlValidTill);
        }
        const current = existing[slot.key];
        if (current?._id) {
          await documentAPI.update(current._id, formData);
        } else {
          await documentAPI.upload(formData);
        }
      }

      if (!files.driving_license && existing.driving_license?._id && dlValidTill) {
        const formData = new FormData();
        formData.append('expiryDate', dlValidTill);
        await documentAPI.update(existing.driving_license._id, formData);
      }

      const personPatch: Record<string, string> = {
        reference1Name: reference1Name.trim(),
        reference1Mobile: reference1Mobile.trim(),
        reference2Name: reference2Name.trim(),
        reference2Mobile: reference2Mobile.trim(),
      };
      if (entityType === 'driver') {
        personPatch.licenseExpiry = dlValidTill;
      }
      await entityApi.update(entityId, personPatch);

      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || `Failed to save ${personLabel.toLowerCase()} documents`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS}px`, overflow: 'hidden' } }}
    >
      <Box sx={{
        px: 3, py: 2, background: brandCss.gradientPrimary, color: '#fff',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
      }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
            {personLabel} documents
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {entityLabel || personLabel}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
            Aadhaar front & back, photo, DL with valid till, and two references
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff', mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {FILE_SLOTS.map((slot) => (
              <Grid item xs={12} sm={6} key={slot.key}>
                <FileSlot
                  label={slot.label}
                  required={slot.required}
                  file={files[slot.key]}
                  existingName={existing[slot.key]?.fileName}
                  onPick={(f) => setSlotFile(slot.key, f)}
                />
              </Grid>
            ))}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="DL Valid till"
                InputLabelProps={{ shrink: true }}
                value={dlValidTill}
                onChange={(e) => setDlValidTill(e.target.value)}
                sx={catchyTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography fontWeight={700} fontSize={14} sx={{ mt: 0.5, mb: 0.5 }}>
                References
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Reference 1 Name"
                value={reference1Name}
                onChange={(e) => setReference1Name(e.target.value)}
                sx={catchyTextFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Reference 1 Mobile"
                value={reference1Mobile}
                onChange={(e) => setReference1Mobile(e.target.value)}
                sx={catchyTextFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Reference 2 Name"
                value={reference2Name}
                onChange={(e) => setReference2Name(e.target.value)}
                sx={catchyTextFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Reference 2 Mobile"
                value={reference2Mobile}
                onChange={(e) => setReference2Mobile(e.target.value)}
                sx={catchyTextFieldSx}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: `1px solid ${brandColors.border}` }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loading}
          sx={{ fontWeight: 700, background: brandCss.gradientPrimary, minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save all'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
